'use client'

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useStudentProgress } from '@/hooks/useStudentProgress'
import { createClientComponentClient } from '@/integrations/supabase/client'
import SubjectProgressCard from '@/components/education/SubjectProgressCard'
import KnowledgeTree from '@/components/education/KnowledgeTree'
import QuizResultsTable from '@/components/education/QuizResultsTable'
import {
    BookOpen, Flame, Trophy, Calendar,
    ChevronRight, Loader2, AlertTriangle,
    BarChart2, GitBranch, GraduationCap, Plus, X, Sparkles, FileText, Award,
} from 'lucide-react'
import { PREP_CATEGORY_IDS } from '@/utils/curriculum'
import { getEntitlements } from '@/utils/entitlements'
import { PlanBadge } from '@/components/subscription/PlanBadge'
import { SageLearnerInsight } from '@/components/education/SageLearnerInsight'
import CertificateGallery from '@/components/education/CertificateGallery'
import CredentialRecordsPanel from '@/components/education/CredentialRecordsPanel'
import { CommunityLibraryModal, cloneCommunityCourse } from '@/components/education/CommunityLibraryBrowser'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'
import BrandLogo from '@/components/common/BrandLogo'
/**
 * StudentProgressPage (/learn) — Phase 5 full implementation pending.
 * This scaffold ensures the route is registered and auth guards work.
 * The full student progress UI (rings, knowledge tree, quiz feed) is built in Phase 5.
 */

// ── Design tokens ──────────────────────────────────────────────────────────────
const SAGE = '#4a7c5e'
const NAVY = '#f8fafc'
const NAVY_MID = '#cbd5e1'
const MUTED = '#94a3b8'
const BORDER = 'rgba(255,255,255,0.08)'
const WHITE = '#ffffff'
const CREAM = '#0a1218'
const CARD_BG = '#101a24'
const SLATE = '#94a3b8'
const BLUE = '#38bdf8'
const EMERALD = '#10b981'
const grad = `linear-gradient(135deg, #183225, #0f2018)`

// Returns SEQUENTIAL module positions (1-based). student_subject_progress.current_unit is
// sequential (receipted); module_plan carries hierarchical IDs — bridge by index, never by value.
// lessons_completed keys exist in multiple historical formats (receipted) — check them all.
function completedModuleIds(subject: { module_plan?: any[] | null; current_unit?: number; lessons_completed?: string[] }): number[] {
    const currentUnit = subject.current_unit ?? 1
    const completed: number[] = []
    if (Array.isArray(subject.module_plan) && subject.module_plan.length > 0) {
        subject.module_plan.forEach((m, i) => {
            const pos = i + 1
            if (pos < currentUnit) { completed.push(pos); return }
            const lessons = Array.isArray(m.lessons) ? m.lessons : []
            if (lessons.length === 0) return
            const doneSet = new Set(subject.lessons_completed ?? [])
            const allDone = lessons.every((l: any, li: number) =>
                doneSet.has(String(l.lesson_id))                    // bare hierarchical id
                || doneSet.has(`u${m.module_id}_l${l.lesson_id}`)   // prefixed (writer format)
                || doneSet.has(`${m.module_id}-${l.lesson_id}`)     // paired
                || doneSet.has(`${pos}-${li + 1}`)                  // sequential
            )
            if (allDone) completed.push(pos)
        })
    } else if (currentUnit > 1) {
        for (let i = 1; i < currentUnit; i++) completed.push(i)
    }
    return completed
}

// ── HeroStat ───────────────────────────────────────────────────────────────────
function HeroStat({ icon, label, value, color, compact }: {
    icon: React.ReactNode; label: string; value: string | number; color: string; compact?: boolean
}) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(14px)',
                borderRadius: compact ? 18 : 24, padding: compact ? '16px 18px' : '24px 28px',
                border: '1px solid rgba(255,255,255,0.15)',
                display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6,
                minWidth: compact ? 130 : 140, boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
        >
            <div style={{ color, marginBottom: compact ? 4 : 8, opacity: 0.9 }}>{icon}</div>
            <p style={{ fontSize: compact ? 26 : 32, fontWeight: 900, color: WHITE, margin: 0, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</p>
            <p style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
                {label}
            </p>
        </motion.div>
    )
}

// ── Tab IDs ────────────────────────────────────────────────────────────────────
const TAB_IDS = ['subjects', 'tree', 'quizzes', 'credentials'] as const
type TabId = typeof TAB_IDS[number]

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function StudentProgressPage() {
    const { t } = useTranslation()
    const { user, userProfile, isLoading: authLoading } = useAuth()
    const navigate = useNavigate()
    const facilityBranding = useFacilityBranding()
    const vocab = useOrgVocab((facilityBranding.org_type || (userProfile as any)?.facility_org_type || userProfile?.facility_type) as any)
    // Stabilise supabase ref — createClientComponentClient() returns a new object
    // each call. An unstabilised reference breaks effect dep arrays and causes
    // fetchProfile / loadEduCategories to re-fire on every render, producing the
    // visible data reload / tab-switch flash.
    const supabaseRef = useRef(createClientComponentClient())
    const supabase = supabaseRef.current

    const {
        subjects, quizResults, streakDays, totalDaysActive,
        subjectsMastered, loading, error, refetch, loadSubjectPlan,
    } = useStudentProgress()

    const [activeTab, setActiveTab] = useState<TabId>('subjects')
    const [selectedSubject, setSelectedSubject] = useState<string | null>(null)
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 800)
    const [academicProfile, setAcademicProfile] = useState<any>(null)
    const [pendingByClass, setPendingByClass] = useState<Record<string, number[]>>({})
    const [hideFamilyBanner, setHideFamilyBanner] = useState<boolean>(() => {
        return Boolean((userProfile?.preferences as any)?.hide_family_hub_banner)
    })
    const [courseFilter, setCourseFilter] = useState<'all' | 'assigned' | 'self_enrolled'>('all')

    const assignedCount = useMemo(() => subjects.filter(s => s.enrollment_type === 'assigned').length, [subjects])
    const selfEnrolledCount = useMemo(() => subjects.filter(s => s.enrollment_type === 'self_enrolled').length, [subjects])

    const displayedSubjects = useMemo(() => {
        if (courseFilter === 'assigned') return subjects.filter(s => s.enrollment_type === 'assigned')
        if (courseFilter === 'self_enrolled') return subjects.filter(s => s.enrollment_type === 'self_enrolled')
        return [...subjects].sort((a, b) => {
            const aAssigned = a.enrollment_type === 'assigned' ? 0 : 1
            const bAssigned = b.enrollment_type === 'assigned' ? 0 : 1
            return aAssigned - bAssigned
        })
    }, [subjects, courseFilter])

    useEffect(() => {
        if ((userProfile?.preferences as any)?.hide_family_hub_banner !== undefined) {
            setHideFamilyBanner(Boolean((userProfile?.preferences as any)?.hide_family_hub_banner))
        }
    }, [userProfile?.preferences])

    // ── Homework reflections: pending badges (A1) ──────────────────────
    useEffect(() => {
        if (!user?.id || subjects.length === 0) return
        let cancelled = false

        async function loadPendingSubmissions() {
            const { data: subRows } = await supabase
                .from('university_submissions')
                .select('class_id, module_id')
                .eq('user_id', user!.id)

            if (cancelled) return

            const submitted = new Map<string, Set<number>>()
            for (const s of subRows ?? []) {
                if (!s.class_id || s.module_id == null) continue
                if (!submitted.has(s.class_id)) submitted.set(s.class_id, new Set())
                submitted.get(s.class_id)!.add(Number(s.module_id))
            }

            const pending: Record<string, number[]> = {}
            for (const subj of subjects) {
                if (!subj.class_id) continue
                if (subj.essay_mode !== 'essay_per_module') continue
                const doneMods = completedModuleIds(subj)
                const unsubmitted = doneMods.filter(pos => {
                    const submittedMods = submitted.get(subj.class_id!) ?? new Set<number>()
                    const hierId = Number(subj.module_plan?.[pos - 1]?.module_id)
                    return !submittedMods.has(pos) && !(Number.isFinite(hierId) && submittedMods.has(hierId))
                })
                if (unsubmitted.length > 0) {
                    pending[subj.class_id] = unsubmitted
                }
            }
            setPendingByClass(pending)
            console.log('[StudentProgressPage] ✅ Pending reflections:', Object.keys(pending).length, 'classes')
        }

        loadPendingSubmissions()
        return () => { cancelled = true }
    }, [user?.id, subjects, supabase])

    // ── On-demand plan fetch for the Knowledge Tree (payload law) ──
    // Non-essay class subjects ship without module_plan in the list query;
    // the tree fetches the real syllabus when one is selected.
    useEffect(() => {
        if (activeTab !== 'tree' || !selectedSubject) return
        const s = subjects.find(x => x.id === selectedSubject)
        if (!s || !s.class_id) return                       // solo subjects ride ai_generated_path
        if (s.essay_mode === 'essay_per_module') return     // essay plans pre-loaded
        if (Array.isArray(s.module_plan) && s.module_plan.length > 0) return
        loadSubjectPlan(s.class_id)
    }, [activeTab, selectedSubject, subjects, loadSubjectPlan])

    const handleDismissFamilyBanner = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setHideFamilyBanner(true)
        if (!user) return
        try {
            const currentPrefs = (userProfile?.preferences as any) || {}
            await supabase
                .from('users')
                .update({
                    preferences: { ...currentPrefs, hide_family_hub_banner: true }
                })
                .eq('id', user.id)
        } catch (err) {
            console.warn('Error saving family banner dismissal:', err)
        }
    }

    const [isEnrollingSelf, setIsEnrollingSelf] = useState(false)
    // ── Community library door state ──
    const [communityOpen, setCommunityOpen] = useState(false)

    const enrolSelfFullPrice = async (packageKey: string) => {
        try {
            setIsEnrollingSelf(true)
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) return

            // 1. Provision first — idempotent, free; billing activates on payment.
            //    Abandoned checkout leaves inert enrolments (the data/access split handles it).
            await fetch(`${(supabase as any).supabaseUrl}/functions/v1/add-family-learner`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                body: JSON.stringify({ is_self: true, self_full_price: true, package_key: packageKey }),
            })

            // 2. Full-price checkout — the billing function every checkout uses.
            //    destination: 'individual' routes to /learn?success=true upon payment.
            const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/billing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                body: JSON.stringify({ action: 'checkout', plan: packageKey, interval: 'month', destination: 'individual' }),
            })
            const data = await res.json()
            if (data?.url) {
                window.location.href = data.url
            }
        } catch (err) {
            console.error('[StudentProgressPage] enrolSelfFullPrice error:', err)
        } finally {
            setIsEnrollingSelf(false)
        }
    }

    useEffect(() => {
        if (!user) return
        async function fetchProfile() {
            const { data } = await supabase
                .from('users')
                .select('global_academic_profile')
                .eq('id', user!.id)
                .maybeSingle()
            if (data?.global_academic_profile) {
                setAcademicProfile(data.global_academic_profile)
            }
        }
        fetchProfile()
    }, [user, supabase])

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 800)
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    // ── Solo learner modal state ──
    const [showNewSubjectModal, setShowNewSubjectModal] = useState(false)
    const [eduCategories, setEduCategories] = useState<{ id: string; name: string; icon: string; parent_id?: string | null; slug?: string | null; target_demographics?: any }[]>([])
    const [selectedCategoryId, setSelectedCategoryId] = useState('')
    const [selectedParentId, setSelectedParentId] = useState('')
    const [showParentDrop, setShowParentDrop] = useState(false)
    const [showChildDrop, setShowChildDrop] = useState(false)
    const [demographicFilter, setDemographicFilter] = useState('all')
    const [targetAge, setTargetAge] = useState(14)
    const [difficultyLevel, setDifficultyLevel] = useState('Secondary')
    const [numLessons, setNumLessons] = useState(5)
    const [focusAreas, setFocusAreas] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const [generatedPlan, setGeneratedPlan] = useState<any>(null)
    const [generateError, setGenerateError] = useState<string | null>(null)

    // Load educational categories when modal opens
    const loadEduCategories = useCallback(async () => {
        const { elite } = getEntitlements(userProfile)
        let query = supabase
            .from('categories')
            .select('id, name, icon, parent_id, slug, target_demographics, category_type')
            .eq('is_educational', true)
            .eq('is_visible', true)
            .order('name')

        if (!elite) {
            // Exclude Elite Academy parent and child categories if user lacks elite entitlement
            query = query.neq('id', 'e0000000-0000-0000-0000-000000000000')
                         .neq('parent_id', 'e0000000-0000-0000-0000-000000000000')
        }

        const { data } = await query
        if (data) setEduCategories(data)
    }, [supabase, userProfile])

    useEffect(() => {
        if (showNewSubjectModal && eduCategories.length === 0) {
            loadEduCategories()
        }
    }, [showNewSubjectModal, eduCategories.length, loadEduCategories])


    const handleOpenNewSubjectModal = () => {
        const { standard, elite } = getEntitlements(userProfile)
        // Exclude Prep subjects when counting for the "1 free subject" limit
        const nonPrepSubjects = subjects.filter(s => !PREP_CATEGORY_IDS.includes(s.category_id))

        if (nonPrepSubjects.length >= 1 && !standard && !elite) {
            alert('You have used your 1 free subject! Please upgrade to continue creating unlimited subjects.');
            navigate('/homeschool/subscription');
            return;
        }
        setShowNewSubjectModal(true);
    }

    // Generate syllabus
    const handleGenerateSyllabus = async () => {
        if (!selectedCategoryId || !user) return
        setIsGenerating(true)
        setGenerateError(null)

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) throw new Error('Not authenticated')

            const selectedCat = eduCategories.find(c => c.id === selectedCategoryId)
            const teacherRequest = `Create a ${difficultyLevel} level curriculum for "${selectedCat?.name}" ` +
                `with ${numLessons} lessons. Target age: ${targetAge}.` +
                (focusAreas ? ` Focus areas: ${focusAreas}` : '')

            const res = await supabase.functions.invoke('generate-syllabus', {
                body: {
                    subject: selectedCat?.name,
                    target_age: targetAge,
                    teacher_request: teacherRequest,
                },
            })

            if (res.error) throw new Error(res.error.message || 'Generation failed')
            setGeneratedPlan(res.data)
        } catch (err) {
            setGenerateError((err as Error).message)
        } finally {
            setIsGenerating(false)
        }
    }

    // Save generated plan to student_subject_progress
    const handleSavePlan = async () => {
        if (!generatedPlan || !selectedCategoryId || !user) return
        setIsGenerating(true)

        try {
            // Check if progress row already exists
            const { data: existing } = await supabase
                .from('student_subject_progress')
                .select('id')
                .eq('user_id', user.id)
                .eq('category_id', selectedCategoryId)
                .maybeSingle()

            if (existing) {
                await supabase
                    .from('student_subject_progress')
                    .update({
                        user_id: user.id,
                        ai_generated_path: generatedPlan.module_plan || generatedPlan
                    })
                    .eq('id', existing.id)
            } else {
                await supabase
                    .from('student_subject_progress')
                    .insert({
                        user_id: user.id,
                        category_id: selectedCategoryId,
                        ai_generated_path: generatedPlan.module_plan || generatedPlan,
                        current_unit: 1,
                        current_lesson: 1,
                    })
            }

            // Reset modal and refresh
            setShowNewSubjectModal(false)
            setGeneratedPlan(null)
            setSelectedCategoryId('')
            setFocusAreas('')
            refetch()

            // Navigate to the subject
            const selectedCat = eduCategories.find(c => c.id === selectedCategoryId)
            if (selectedCat) {
                const slug = selectedCat.slug || selectedCat.name.toLowerCase()
                    .replace(/&/g, 'and')
                    .replace(/[\/]+/g, '-')
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '')

                navigate(`/learn/session?category=${selectedCat.id}`)
            }
        } catch (err) {
            setGenerateError((err as Error).message)
        } finally {
            setIsGenerating(false)
        }
    }

    // ── Auth guard ──
    if (authLoading || loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: CREAM }}>
                <div style={{ textAlign: 'center' }}>
                    <Loader2 size={36} color={BLUE} className="animate-spin" style={{ margin: '0 auto 12px', display: 'block' }} />
                    <p style={{ color: MUTED }}>{t('education.common.loading')}</p>
                </div>
            </div>
        )
    }

    if (!user) {
        return <Navigate to="/auth/login" replace />
    }

    if (error) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: CREAM, padding: 24 }}>
                <div style={{ textAlign: 'center' }}>
                    <AlertTriangle size={40} color="#dc2626" style={{ margin: '0 auto 12px', display: 'block' }} />
                    <p style={{ color: NAVY, fontWeight: 700, marginBottom: 8 }}>{t('education.progress.error.fetchFailed')}</p>
                    <button onClick={refetch} style={{
                        padding: '10px 24px', background: BLUE, color: WHITE,
                        border: 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 700,
                    }}>
                        {t('education.common.retry')}
                    </button>
                </div>
            </div>
        )
    }

    const subjectNames = [...new Set(subjects.map(s => s.class_name ?? s.category_name))]
    const selectedSubjectData = selectedSubject
        ? subjects.find(s => s.id === selectedSubject) ?? null
        : null
    const heroSubject = subjects[0] ?? null

    const TABS = [
        { id: 'subjects' as TabId, label: facilityBranding.is_branded ? vocab.coursePlural : t('education.progress.tabs.subjects'), icon: <BookOpen size={16} /> },
        { id: 'tree' as TabId, label: t('education.progress.tabs.tree'), icon: <GitBranch size={16} /> },
        { id: 'quizzes' as TabId, label: t('education.progress.tabs.quizzes'), icon: <BarChart2 size={16} /> },
        { id: 'credentials' as TabId, label: 'Records & Credentials', icon: <GraduationCap size={16} /> },
    ]

    return (
        <div style={{ minHeight: '100dvh', background: CREAM }}>

            {/* ── HERO BAND ── */}
            <div style={{
                background: facilityBranding.is_branded && facilityBranding.primary_color
                    ? `linear-gradient(135deg, ${facilityBranding.primary_color}, #0a1218)`
                    : grad,
                paddingBottom: 0,
            }}>
                <div style={{ maxWidth: 1040, margin: '0 auto', padding: isMobile ? '20px 16px 18px' : '28px 20px 24px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, marginBottom: isMobile ? 14 : 20 }}>
                        {!isMobile && (
                        <button
                            onClick={() => navigate('/learn')}
                            aria-label={t('education.common.backToDashboard')}
                            style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#94a3b8',
                            }}
                        >
                            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                        </button>
                        )}
                        {facilityBranding.is_branded && facilityBranding.logo_url ? (
                            <BrandLogo
                                src={facilityBranding.logo_url}
                                alt={facilityBranding.display_name}
                                style={{ width: 44, height: 44, objectFit: 'contain' }}
                            />
                        ) : (
                            <div style={{
                                width: 44, height: 44, borderRadius: 14,
                                background: 'rgba(59,91,219,0.4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <GraduationCap size={22} color="#60a5fa" />
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <h1 style={{ fontSize: isMobile ? 17 : 22, fontWeight: 900, color: WHITE, margin: 0, lineHeight: 1.25 }}>
                                    {facilityBranding.is_branded ? `${facilityBranding.display_name}: ${vocab.learnerPortalName}` : t('education.progress.pageTitle')}
                                </h1>
                                <PlanBadge userProfile={userProfile} />
                            </div>
                            <p style={{ fontSize: 12, color: '#60a5fa', margin: '2px 0 0' }}>
                                {facilityBranding.is_branded
                                    ? `Welcome, ${userProfile?.name?.split(' ')[0] || vocab.learnerSingular} · ${vocab.hubTagline}`
                                    : t('education.progress.welcomeBack', { name: userProfile?.name?.split(' ')[0] || 'Learner' })}
                            </p>
                        </div>
                        {/* My Transcript — solo learners + platform members (adults manage their own records) */}
                        {!isMobile && (userProfile?.facility_role !== 'member' || userProfile?.facility_type === 'platform') && subjects.length > 0 && (
                            <button
                                onClick={() => navigate('/record/me')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 14px', borderRadius: 12,
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: WHITE, fontSize: 12.5, fontWeight: 700,
                                    cursor: 'pointer', flexShrink: 0,
                                }}
                                title="View & print your learning transcript"
                            >
                                <FileText size={14} /> My Transcript
                            </button>
                        )}
                    </div>

                    {/* Hero stats — grid keeps cards equal-width (2×2 on phones, 4-up on desktop) */}
                    <div style={{
                        display: 'grid', gap: isMobile ? 10 : 12,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    }}>
                        <HeroStat compact={isMobile} icon={<Flame size={20} color="#fb923c" />} label={t('education.progress.heroStats.dayStreak')} value={streakDays} color="#fb923c" />
                        <HeroStat compact={isMobile} icon={<Trophy size={20} color="#fbbf24" />} label={t('education.progress.heroStats.mastered')} value={subjectsMastered} color="#fbbf24" />
                        <HeroStat compact={isMobile} icon={<Calendar size={20} color="#34d399" />} label={t('education.progress.heroStats.daysActive')} value={totalDaysActive} color="#34d399" />
                        <HeroStat compact={isMobile} icon={<BookOpen size={20} color="#60a5fa" />} label={facilityBranding.is_branded ? vocab.subjectStatLabel : t('education.progress.heroStats.subjects')} value={subjects.length} color="#60a5fa" />
                    </div>

                    {/* Quick continue CTA */}
                    {heroSubject && (
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 0.95, y: 0 }}
                            whileHover={{ opacity: 1 }}
                            onClick={() => {
                                let url = `/learn/session?category=${heroSubject.category_id}`;
                                if (heroSubject.class_id) url += `&class=${heroSubject.class_id}`;
                                navigate(url);
                            }}
                            style={{
                                marginTop: 20, display: 'flex', alignItems: 'center', gap: 10,
                                background: 'rgba(255,255,255,0.08)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 14, padding: '12px 18px', cursor: 'pointer', color: WHITE,
                                width: '100%', maxWidth: 400, textAlign: 'left',
                            }}
                        >
                            <span style={{ fontSize: 22 }}>{heroSubject.category_icon}</span>
                            <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: 700, margin: 0, fontSize: 14 }}>
                                    {t('education.progress.continueCta', { subject: heroSubject.class_name ?? heroSubject.category_name })}
                                </p>
                                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                                    {t('education.progress.continueCtaSub', {
                                        unit: heroSubject.current_unit,
                                        lesson: heroSubject.current_lesson,
                                        mastery: Math.round(heroSubject.mastery_score),
                                    })}
                                </p>
                            </div>
                            <ChevronRight size={18} color="rgba(255,255,255,0.5)" />
                        </motion.button>
                    )}
                </div>

                {/* Wave divider */}
                <svg viewBox="0 0 1440 40" style={{ display: 'block', width: '100%', height: 40 }} preserveAspectRatio="none">
                    <path d="M0,40 C360,0 1080,40 1440,15 L1440,40 Z" fill={CREAM} />
                </svg>
            </div>

            {/* ── PREP COLLEGE BANNER (shown to free/solo users when subjects are enrolled) ── */}
            {!userProfile?.facility_id && subjects.length > 0 && (
                <div style={{
                    maxWidth: 1040, margin: '0 auto', padding: '16px 20px 0',
                    display: 'flex', flexWrap: 'wrap', gap: 12,
                }}>
                    {/* Prep College entry card */}
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            flex: '1 1 300px', background: `linear-gradient(135deg, #1e3a5f, #0f2744)`,
                            borderRadius: 18, padding: '18px 22px',
                            border: '1px solid rgba(100,170,255,0.2)',
                            display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        }}
                        onClick={() => navigate('/prep')}
                        whileHover={{ scale: 1.01 }}
                    >
                        <div style={{
                            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                            background: 'rgba(96,165,250,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                        }}>🎓</div>
                        <div style={{ flex: 1 }}>
                            <p style={{ fontWeight: 800, color: '#e2e8f0', margin: 0, fontSize: 15 }}>{t('common.prep_college_free_foundation_subjects')}</p>
                            <p style={{ fontSize: 12, color: '#94a3b8', margin: '3px 0 0' }}>{t('common.critical_thinking_study_skills_self_awareness')}</p>
                        </div>
                        <ChevronRight size={18} color="#60a5fa" />
                    </motion.div>

                    {/* Upgrade banner */}
                    {!getEntitlements(userProfile).isPaid && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                flex: '1 1 280px', background: `linear-gradient(135deg, #0c4a6e, #082f49)`,
                                borderRadius: 18, padding: '18px 22px',
                                border: '1px solid rgba(14,165,233,0.3)',
                                display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            }}
                            onClick={() => navigate('/homeschool/subscription')}
                            whileHover={{ scale: 1.01 }}
                        >
                            <div style={{
                                width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                                background: 'rgba(14,165,233,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                            }}>✨</div>
                            <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: 800, color: '#e0f2fe', margin: 0, fontSize: 15 }}>Unlock 84+ Subjects from $35/mo</p>
                                <p style={{ fontSize: 12, color: '#bae6fd', margin: '3px 0 0' }}>Standard · Elite Academy · Complete Bundle, Simple, honest pricing.</p>
                            </div>
                            <ChevronRight size={18} color="#7dd3fc" />
                        </motion.div>
                    )}
                </div>
            )}

            {/* ── AI ACADEMIC OVERSEER PROFILE CARD ── */}
            {academicProfile?.summary && (
                <div style={{ maxWidth: 1040, margin: '20px auto 0', padding: '0 20px' }}>
                    <SageLearnerInsight
                        profile={academicProfile as any}
                        learnerName="your"
                    />
                </div>
            )}

            {/* ── SPONSORSHIP BANNER ── */}
            {userProfile?.sponsor_id && (
                <div style={{
                    maxWidth: 1040, margin: '0 auto', padding: '12px 20px 0',
                }}>
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            background: 'linear-gradient(135deg, #065f46, #064e3b)',
                            borderRadius: 18, padding: '16px 22px',
                            border: '1px solid rgba(52,211,153,0.3)',
                            display: 'flex', gap: 14, alignItems: 'center',
                        }}
                    >
                        <div style={{
                            width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                            background: 'rgba(52,211,153,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                        }}>💚</div>
                        <div style={{ flex: 1 }}>
                            <p style={{ fontWeight: 800, color: '#d1fae5', margin: 0, fontSize: 14 }}>
                                You have sponsored access{userProfile.active_plan ? `, ${userProfile.active_plan.replace(/_/g, ' ')}` : ''}
                            </p>
                            <p style={{ fontSize: 12, color: '#6ee7b7', margin: '2px 0 0' }}>
                                Your learning is supported by a generous sponsor. Make the most of it!
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ── Community library modal — parent clones, member/solo read-only ── */}
            <CommunityLibraryModal
                open={communityOpen}
                onClose={() => setCommunityOpen(false)}
                canClone={!!userProfile?.facility_id && userProfile?.facility_role === 'admin'}
                onClone={userProfile?.facility_id && userProfile?.facility_role === 'admin' ? async (course, className) => {
                    const res = await cloneCommunityCourse(course.id,
                        { teacherId: user!.id, facilityId: userProfile!.facility_id! }, className)
                    if (res.error) throw new Error(res.error)
                } : undefined}
                readOnlyNote="Ask your parent or teacher to add this course to your classes."
            />

            {/* ── CONTENT ── */}
            <div style={{ maxWidth: 1040, margin: '0 auto', padding: isMobile ? '8px 16px 88px' : '8px 20px 80px' }}>

                {/* Tab bar */}
                <div style={{
                    display: isMobile ? 'grid' : 'flex', gap: isMobile ? 6 : 4, marginBottom: 28,
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined,
                    background: CARD_BG, borderRadius: 16, padding: 4,
                    border: `1px solid ${BORDER}`, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            id={`edu-tab-${tab.id}`}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: isMobile ? 'none' : 1, padding: isMobile ? '10px 6px' : '10px 12px', borderRadius: 12,
                                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                                background: activeTab === tab.id ? grad : 'transparent',
                                color: activeTab === tab.id ? WHITE : MUTED,
                                fontWeight: activeTab === tab.id ? 700 : 500,
                                fontSize: isMobile ? 12.5 : 13,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                transition: 'all 0.15s',
                                boxShadow: activeTab === tab.id ? `0 4px 16px ${BLUE}40` : 'none',
                            }}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── SUBJECTS TAB ── */}
                {activeTab === 'subjects' && (
                    <div>
                        {subjects.length === 0 ? (
                            userProfile?.facility_role === 'member' ? (
                                /* ── MEMBER (CHILD) EMPTY STATE ── */
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{
                                        background: CARD_BG, borderRadius: 24,
                                        padding: '60px 32px', textAlign: 'center',
                                        border: `1px solid ${BORDER}`,
                                    }}
                                >
                                    <div style={{
                                        width: 72, height: 72, borderRadius: 20, margin: '0 auto 20px',
                                        background: `${BLUE}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <BookOpen size={32} color={BLUE} />
                                    </div>
                                    <h2 style={{ fontWeight: 800, color: NAVY, marginBottom: 10, fontSize: 22 }}>
                                        {facilityBranding.is_branded || userProfile?.facility_id
                                            ? `No ${vocab.coursePlural} Assigned Yet`
                                            : t('education.progress.noSubjects.titleHub', { defaultValue: 'No Classes Assigned Yet' })}
                                    </h2>
                                    <p style={{ color: MUTED, maxWidth: 420, margin: '0 auto', fontSize: 15, lineHeight: 1.6 }}>
                                        {facilityBranding.is_branded || userProfile?.facility_id
                                            ? `Your ${vocab.staffSingular.toLowerCase()} will assign ${vocab.coursePlural.toLowerCase()} for you. They will appear here once ready.`
                                            : userProfile?.facility_type === 'school'
                                            ? t('education.progress.noSubjects.descriptionHubSchool', {
                                                defaultValue: 'Your teacher will assign subjects from the school dashboard. They will appear here once ready.'
                                              })
                                            : (userProfile?.facility_type === 'family_edu' || userProfile?.facility_type === 'homeschool')
                                            ? t('education.progress.noSubjects.descriptionHubFamily', {
                                                defaultValue: 'Your parent or teacher will add subjects for you soon. They will appear here once ready.'
                                              })
                                            : t('education.progress.noSubjects.descriptionSolo', {
                                                defaultValue: 'Subjects will appear here once added. Browse the catalogue to get started.'
                                              })
                                        }
                                    </p>
                                    <button onClick={() => setCommunityOpen(true)}
                                        style={{ marginTop: 16, background: 'none', border: 'none', cursor: 'pointer', color: SAGE, fontWeight: 700, fontSize: 13.5 }}>
                                        🌍 Browse community electives →
                                    </button>
                                    {userProfile?.facility_type === 'platform' && (
                                        <button onClick={() => navigate('/learn/subjects')}
                                            style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', color: BLUE, fontWeight: 700, fontSize: 13.5 }}>
                                            📚 Browse the course catalogue →
                                        </button>
                                    )}
                                </motion.div>
                            ) : facilityBranding.is_branded ? (
                                /* ── WHITELABEL ORG ADMIN/STAFF EMPTY STATE ── */
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{ background: CARD_BG, borderRadius: 24, padding: '44px 32px', border: `1px solid ${BORDER}`, textAlign: 'center' }}
                                >
                                    <div style={{
                                        width: 60, height: 60, borderRadius: 18, margin: '0 auto 16px',
                                        background: 'rgba(56,189,248,0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <BookOpen size={28} color="#38bdf8" />
                                    </div>
                                    <h2 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 24 }}>
                                        Manage your {vocab.coursePlural.toLowerCase()}
                                    </h2>
                                    <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.5, margin: '0 auto 20px', maxWidth: 460 }}>
                                        Create {vocab.coursePlural.toLowerCase()}, upload knowledge files, and assign them to your {vocab.learnerPlural.toLowerCase()} from your admin console.
                                    </p>
                                    <button
                                        onClick={() => navigate('/university-brand/admin')}
                                        style={{
                                            padding: '12px 24px', borderRadius: 14,
                                            background: facilityBranding.primary_color || '#0ea5e9',
                                            color: WHITE, border: 'none', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                                        }}
                                    >
                                        Go to {facilityBranding.display_name || 'Admin'} Console →
                                    </button>
                                </motion.div>
                            ) : userProfile?.facility_id ? (
                                /* ── PARENT (ADMIN) EMPTY STATE — NEW ── */
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{ background: CARD_BG, borderRadius: 24, padding: '44px 32px', border: `1px solid ${BORDER}` }}
                                >
                                    <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto', marginBottom: 28 }}>
                                        <div style={{
                                            width: 60, height: 60, borderRadius: 18, margin: '0 auto 16px',
                                            background: 'linear-gradient(135deg, rgba(212,168,67,0.15), rgba(59,130,246,0.15))',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <GraduationCap size={28} color={SAGE} />
                                        </div>
                                        <h2 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 24 }}>
                                            Your own learning journey
                                        </h2>
                                        <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.5, margin: 0 }}>
                                            Study any subject yourself, alongside your children or on your own.
                                            Try free Prep lessons, browse the catalog, or unlock full access.
                                        </p>
                                    </div>

                                    {/* Two doors + upsell, side by side */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 24 }}>
                                        {/* Door 1: Free Prep */}
                                        <div onClick={() => navigate('/prep')}
                                            className="hover:shadow-md transition-all"
                                            style={{ background: 'linear-gradient(135deg, #131d27, #172532)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 18, padding: '22px 18px', cursor: 'pointer' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <span style={{ fontSize: 26 }}>🎓</span>
                                                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, background: 'rgba(56,189,248,0.15)', color: '#38bdf8' }}>
                                                    Free · No Card
                                                </span>
                                            </div>
                                            <h3 style={{ fontSize: 16, fontWeight: 800, color: NAVY, margin: '0 0 5px' }}>{t('common.try_free_prep')}</h3>
                                            <p style={{ fontSize: 12.5, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                4 foundation courses, critical thinking, study skills, logic & self-awareness.
                                            </p>
                                        </div>

                                        {/* Door 2: Browse catalog */}
                                        <div onClick={() => navigate('/learn/subjects')}
                                            id="edu-parent-browse-btn"
                                            className="hover:shadow-md transition-all"
                                            style={{ background: 'linear-gradient(135deg, #112238, #162c47)', border: '1.5px solid rgba(56,189,248,0.4)', borderRadius: 18, padding: '22px 18px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <span style={{ fontSize: 26 }}>📚</span>
                                                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, background: '#2563eb', color: WHITE }}>
                                                    1 Free Subject
                                                </span>
                                            </div>
                                            <h3 style={{ fontSize: 16, fontWeight: 800, color: NAVY, margin: '0 0 5px' }}>{userProfile?.facility_type === 'platform' ? 'Browse the Full Catalog' : 'Browse 84+ Subjects'}</h3>
                                            <p style={{ fontSize: 12.5, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                {userProfile?.facility_type === 'platform'
                                                    ? 'Full catalog access is included with your organisation\'s seats.'
                                                    : 'Your first subject is free, AI tutoring and quizzes included.'}
                                            </p>
                                        </div>

                                        {/* Door 3: Community Library — free teacher-built electives */}
                                        <div onClick={() => setCommunityOpen(true)}
                                            id="edu-parent-community-btn"
                                            className="hover:shadow-md transition-all"
                                            style={{ background: 'linear-gradient(135deg, #11261e, #163327)', border: '1.5px solid rgba(74,124,94,0.45)', borderRadius: 18, padding: '22px 18px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <span style={{ fontSize: 26 }}>🌍</span>
                                                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, background: '#4a7c5e', color: WHITE }}>
                                                    Free · New
                                                </span>
                                            </div>
                                            <h3 style={{ fontSize: 16, fontWeight: 800, color: NAVY, margin: '0 0 5px' }}>{t('common.community_electives')}</h3>
                                            <p style={{ fontSize: 12.5, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                Gardening, archery, first aid, robotics, courses built by teachers, free for every family.
                                            </p>
                                        </div>
                                    </div>

                                    {/* The upsell CTA — correct variant per payment state */}
                                    {getEntitlements(userProfile).isPaid ? (
                                        <button onClick={() => navigate('/homeschool/parent')}
                                            style={{ width: '100%', padding: '14px 20px', borderRadius: 14, border: 'none', cursor: 'pointer', background: '#D4A843', color: '#000', fontWeight: 800, fontSize: 14 }}>
                                            Add My Studies at 50% off, from your Parent Hub →
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => enrolSelfFullPrice('homeschool_single')}
                                            disabled={isEnrollingSelf}
                                            style={{
                                                width: '100%', padding: '14px 20px', borderRadius: 14, border: 'none',
                                                cursor: isEnrollingSelf ? 'wait' : 'pointer',
                                                background: `linear-gradient(135deg, #7a5c20, #A8842C)`,
                                                color: '#fff', fontWeight: 800, fontSize: 14,
                                                opacity: isEnrollingSelf ? 0.7 : 1,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                            }}
                                        >
                                            {isEnrollingSelf ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    Preparing Your Learning...
                                                </>
                                            ) : (
                                                'Enrol Yourself: from $35/mo →'
                                            )}
                                        </button>
                                    )}
                                </motion.div>
                            ) : (
                                /* ── FACILITY-LESS (SOLO / COLD SIGNUP) EMPTY STATE ── */
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{
                                        background: WHITE, borderRadius: 24,
                                        padding: isMobile ? '36px 20px' : '52px 36px',
                                        border: `1px solid ${BORDER}`,
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.03)',
                                    }}
                                >
                                    <div style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
                                        <div style={{
                                            width: 68, height: 68, borderRadius: 20, margin: '0 auto 18px',
                                            background: 'linear-gradient(135deg, rgba(74,124,94,0.15) 0%, rgba(59,130,246,0.15) 100%)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Sparkles size={32} color={SAGE} />
                                        </div>
                                        <h2 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: isMobile ? 22 : 26 }}>
                                            {userProfile?.name?.trim()?.split(/\s+/)[0]
                                                ? `Welcome, ${userProfile.name.trim().split(/\s+/)[0]}: let's start learning`
                                                : 'Your learning journey starts here'
                                            }
                                        </h2>
                                        <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.5, margin: '0 auto' }}>
                                            Choose where to begin: explore free foundational lessons, pick your first academic course from the catalog, or unlock full academy access.
                                        </p>
                                    </div>

                                    {/* 3 Clear Doors */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                        gap: 16,
                                        marginTop: 32,
                                    }}>
                                        {/* Door 1: Free Prep (Free taste) */}
                                        <div
                                            onClick={() => navigate('/prep')}
                                            style={{
                                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                border: '1.5px solid #cbd5e1',
                                                borderRadius: 20,
                                                padding: '24px 20px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            className="hover:shadow-md hover:border-slate-400 transition-all"
                                        >
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                    <span style={{ fontSize: 30 }}>🎓</span>
                                                    <span style={{
                                                        fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase',
                                                        letterSpacing: 0.5, padding: '3px 9px', borderRadius: 20,
                                                        background: '#e0f2fe', color: '#0369a1',
                                                    }}>
                                                        Free · No Card
                                                    </span>
                                                </div>
                                                <h3 style={{ fontSize: 17, fontWeight: 800, color: NAVY, margin: '0 0 6px' }}>
                                                    {t('common.try_free_prep')}
                                                </h3>
                                                <p style={{ fontSize: 13, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                    4 foundational courses in critical thinking, study skills, logic & self-awareness. Always free.
                                                </p>
                                            </div>
                                            <div style={{
                                                marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                fontSize: 13.5, fontWeight: 800, color: '#0284c7',
                                            }}>
                                                <span>{t('common.start_free_prep')}</span>
                                                <ChevronRight size={17} />
                                            </div>
                                        </div>

                                        {/* Door 2: Browse Subjects (Free trial) */}
                                        <div
                                            id="edu-browse-subjects-btn"
                                            onClick={() => navigate('/learn/subjects')}
                                            style={{
                                                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                                border: '2px solid #60a5fa',
                                                borderRadius: 20,
                                                padding: '24px 20px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 4px 16px rgba(59,130,246,0.12)',
                                            }}
                                            className="hover:shadow-lg hover:border-blue-500 transition-all"
                                        >
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                    <span style={{ fontSize: 30 }}>📚</span>
                                                    <span style={{
                                                        fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase',
                                                        letterSpacing: 0.5, padding: '3px 9px', borderRadius: 20,
                                                        background: '#2563eb', color: WHITE,
                                                    }}>
                                                        1 Free Subject
                                                    </span>
                                                </div>
                                                <h3 style={{ fontSize: 17, fontWeight: 800, color: NAVY, margin: '0 0 6px' }}>
                                                    Browse 84+ Subjects
                                                </h3>
                                                <p style={{ fontSize: 13, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                    Choose any course from our full academic catalog. AI tutoring and interactive quizzes included.
                                                </p>
                                            </div>
                                            <div style={{
                                                marginTop: 20, paddingTop: 14, borderTop: '1px solid #bfdbfe',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                fontSize: 13.5, fontWeight: 800, color: '#1d4ed8',
                                            }}>
                                                <span>{t('common.browse_catalog')}</span>
                                                <ChevronRight size={17} />
                                            </div>
                                        </div>

                                        {/* Door 3: Complete Academy (Upgrade) */}
                                        <div
                                            onClick={() => navigate('/homeschool/subscription')}
                                            style={{
                                                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                                                border: '1.5px solid #38bdf8',
                                                borderRadius: 20,
                                                padding: '24px 20px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            className="hover:shadow-md hover:border-sky-400 transition-all"
                                        >
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                    <span style={{ fontSize: 30 }}>✨</span>
                                                    <span style={{
                                                        fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase',
                                                        letterSpacing: 0.5, padding: '3px 9px', borderRadius: 20,
                                                        background: '#0284c7', color: WHITE,
                                                    }}>
                                                        From $35/mo
                                                    </span>
                                                </div>
                                                <h3 style={{ fontSize: 17, fontWeight: 800, color: NAVY, margin: '0 0 6px' }}>
                                                    Unlock All Courses
                                                </h3>
                                                <p style={{ fontSize: 13, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                                                    Full access to all 84 subjects, 30 elite leadership courses, certificates, and advanced AI tutor guidance.
                                                </p>
                                            </div>
                                            <div style={{
                                                marginTop: 20, paddingTop: 14, borderTop: '1px solid #bae6fd',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                fontSize: 13.5, fontWeight: 800, color: '#0369a1',
                                            }}>
                                                <span>{t('common.view_all_plans')}</span>
                                                <ChevronRight size={17} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Parent Hub Escape Hatch Banner */}
                                    {!hideFamilyBanner && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            style={{
                                                marginTop: 24,
                                                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                                                borderRadius: 18,
                                                padding: '18px 22px',
                                                border: '1px solid rgba(255,255,255,0.12)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: 16,
                                                position: 'relative',
                                                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                                            }}
                                        >
                                            <button
                                                onClick={handleDismissFamilyBanner}
                                                aria-label={t('common.dismiss_banner')}
                                                style={{
                                                    position: 'absolute', top: 10, right: 10,
                                                    background: 'rgba(255,255,255,0.1)', border: 'none',
                                                    borderRadius: '50%', width: 24, height: 24,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                                                }}
                                            >
                                                <X size={13} />
                                            </button>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, maxWidth: 520 }}>
                                                <div style={{
                                                    width: 44, height: 44, borderRadius: 12,
                                                    background: 'rgba(212,168,67,0.15)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 22, flexShrink: 0,
                                                }}>
                                                    👨‍👩‍👧
                                                </div>
                                                <div>
                                                    <p style={{ fontWeight: 800, color: WHITE, fontSize: 14.5, margin: 0 }}>
                                                        Learning as a family?
                                                    </p>
                                                    <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', margin: '3px 0 0', lineHeight: 1.4 }}>
                                                        Set up a Parent Hub to manage children, assign subjects, and track progress.
                                                    </p>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => navigate('/homeschool/enrol')}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '9px 18px', borderRadius: 12,
                                                    background: '#d4a843', color: '#000000',
                                                    fontWeight: 800, fontSize: 13, border: 'none',
                                                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(212,168,67,0.3)',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                Create Family Hub <ChevronRight size={15} />
                                            </button>
                                        </motion.div>
                                    )}
                                </motion.div>
                            )
                        ) : (
                            <div>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginBottom: 24, flexWrap: 'wrap', gap: 14,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                            onClick={() => setCourseFilter('all')}
                                            style={{
                                                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 800,
                                                border: courseFilter === 'all' ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
                                                cursor: 'pointer', transition: 'all 0.15s ease',
                                                background: courseFilter === 'all' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.04)',
                                                color: courseFilter === 'all' ? '#ffffff' : MUTED,
                                            }}
                                        >
                                            All {vocab.coursePlural} ({subjects.length})
                                        </button>
                                        {assignedCount > 0 && (
                                            <button
                                                onClick={() => setCourseFilter('assigned')}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 800,
                                                    border: courseFilter === 'assigned' ? '1px solid rgba(245,158,11,0.5)' : '1px solid transparent',
                                                    cursor: 'pointer', transition: 'all 0.15s ease',
                                                    background: courseFilter === 'assigned' ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.06)',
                                                    color: courseFilter === 'assigned' ? '#fbbf24' : '#f59e0b',
                                                }}
                                            >
                                                <Award size={13} /> Assigned ({assignedCount})
                                            </button>
                                        )}
                                        {selfEnrolledCount > 0 && (
                                            <button
                                                onClick={() => setCourseFilter('self_enrolled')}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 800,
                                                    border: courseFilter === 'self_enrolled' ? '1px solid rgba(56,189,248,0.5)' : '1px solid transparent',
                                                    cursor: 'pointer', transition: 'all 0.15s ease',
                                                    background: courseFilter === 'self_enrolled' ? 'rgba(56,189,248,0.18)' : 'rgba(56,189,248,0.06)',
                                                    color: courseFilter === 'self_enrolled' ? '#38bdf8' : '#38bdf8cc',
                                                }}
                                            >
                                                <BookOpen size={13} /> Self Enrolled ({selfEnrolledCount})
                                            </button>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {/* My Transcript — quick link for solo learners + platform members */}
                                        {(userProfile?.facility_role !== 'member' || userProfile?.facility_type === 'platform') && (
                                            <button
                                                onClick={() => navigate('/record/me')}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '8px 14px', borderRadius: 12,
                                                    background: `${SAGE}12`,
                                                    color: SAGE, border: `1px solid ${SAGE}30`,
                                                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                }}
                                                title="View and print your learning record"
                                            >
                                                <FileText size={15} /> My Transcript
                                            </button>
                                        )}
                                        <button
                                            id="edu-browse-catalog-btn"
                                            onClick={() => navigate('/learn/subjects')}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                padding: '8px 16px', borderRadius: 12,
                                                background: `${BLUE}12`,
                                                color: BLUE, border: `1px solid ${BLUE}30`,
                                                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            <BookOpen size={15} /> {isMobile ? 'Browse Catalog' : `Browse Catalog (>120 ${vocab.coursePlural})`}
                                        </button>
                                    </div>
                                </div>

                                {courseFilter === 'all' && assignedCount > 0 && selfEnrolledCount > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                                        {/* Assigned Section */}
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 8,
                                                    background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.25)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                }}>
                                                    <Award size={15} color="#f59e0b" />
                                                </div>
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: NAVY, letterSpacing: '-0.01em' }}>
                                                        Assigned {vocab.coursePlural} ({assignedCount})
                                                    </h3>
                                                    <p style={{ margin: 0, fontSize: 12, color: MUTED }}>
                                                        Required {vocab.coursePlural.toLowerCase()} assigned to you by your {vocab.adminTitle || 'organization or instructor'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
                                                gap: isMobile ? 16 : 28,
                                            }}>
                                                {subjects.filter(s => s.enrollment_type === 'assigned').map((s, i) => (
                                                    <SubjectProgressCard
                                                        key={s.id}
                                                        subject={s}
                                                        index={i}
                                                        pendingReflections={s.class_id ? pendingByClass[s.class_id] : undefined}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Self-Enrolled Section */}
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 8,
                                                    background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.25)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                }}>
                                                    <BookOpen size={15} color="#38bdf8" />
                                                </div>
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: NAVY, letterSpacing: '-0.01em' }}>
                                                        Self-Enrolled {vocab.coursePlural} ({selfEnrolledCount})
                                                    </h3>
                                                    <p style={{ margin: 0, fontSize: 12, color: MUTED }}>
                                                        Elective {vocab.coursePlural.toLowerCase()} you enrolled in from the catalog for independent study
                                                    </p>
                                                </div>
                                            </div>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
                                                gap: isMobile ? 16 : 28,
                                            }}>
                                                {subjects.filter(s => s.enrollment_type === 'self_enrolled').map((s, i) => (
                                                    <SubjectProgressCard
                                                        key={s.id}
                                                        subject={s}
                                                        index={i}
                                                        pendingReflections={s.class_id ? pendingByClass[s.class_id] : undefined}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : displayedSubjects.length > 0 ? (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
                                        gap: isMobile ? 16 : 28,
                                    }}>
                                        {displayedSubjects.map((s, i) => (
                                            <SubjectProgressCard
                                                key={s.id}
                                                subject={s}
                                                index={i}
                                                pendingReflections={s.class_id ? pendingByClass[s.class_id] : undefined}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{
                                        background: 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${BORDER}`,
                                        borderRadius: 20,
                                        padding: '40px 24px',
                                        textAlign: 'center',
                                        maxWidth: 500,
                                        margin: '20px auto',
                                    }}>
                                        <p style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 800, color: NAVY }}>
                                            {courseFilter === 'assigned' ? `No Assigned ${vocab.coursePlural} Found` : `No Self-Enrolled ${vocab.coursePlural} Found`}
                                        </p>
                                        <p style={{ margin: '0 0 20px 0', fontSize: 13, color: MUTED }}>
                                            {courseFilter === 'assigned'
                                                ? `You have not been assigned any mandatory ${vocab.coursePlural.toLowerCase()} at this time.`
                                                : `You have not self-enrolled in any catalog electives yet.`}
                                        </p>
                                        {courseFilter === 'self_enrolled' && (
                                            <button
                                                onClick={() => navigate('/learn/subjects')}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '9px 18px', borderRadius: 12,
                                                    background: BLUE, color: '#000000',
                                                    fontSize: 13, fontWeight: 800, border: 'none',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <BookOpen size={15} /> Browse Catalog
                                            </button>
                                        )}
                                    </div>
                                )}

                                {!userProfile?.facility_id && (
                                    <button
                                        onClick={() => navigate('/learn/subjects')}
                                        style={{
                                            position: 'fixed', bottom: 24, right: 24,
                                            width: 56, height: 56, borderRadius: '50%',
                                            background: grad, color: WHITE, border: 'none',
                                            boxShadow: `0 6px 20px ${BLUE}40`,
                                            cursor: 'pointer', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            zIndex: 50,
                                        }}
                                        title="Browse & Add Courses"
                                    >
                                        <Plus size={24} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── LEARNING PATH TAB ── */}
                {activeTab === 'tree' && (
                    <div>
                        {subjects.length === 0 ? (
                            <div style={{
                                background: CREAM, borderRadius: 24, padding: '48px 32px',
                                textAlign: 'center', border: `1px solid ${BORDER}`,
                            }}>
                                <p style={{ color: MUTED }}>{t('education.progress.treeNoSubjects')}</p>
                            </div>
                        ) : (
                            <div>
                                {/* Subject selector */}
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                                    {subjects.map(s => (
                                        <button
                                            key={s.id}
                                            id={`edu-subject-select-${s.id}`}
                                            onClick={() => setSelectedSubject(s.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 6,
                                                padding: '8px 16px', borderRadius: 12,
                                                border: `2px solid ${selectedSubject === s.id ? BLUE : BORDER}`,
                                                background: selectedSubject === s.id ? `${BLUE}10` : CREAM,
                                                color: selectedSubject === s.id ? BLUE : BLUE,
                                                fontWeight: selectedSubject === s.id ? 700 : 500,
                                                fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                                            }}
                                        >
                                            <span>{s.category_icon}</span>
                                            <span>{s.class_name ?? s.category_name}</span>
                                        </button>
                                    ))}
                                </div>

                                <div style={{
                                    background: CREAM, borderRadius: 24,
                                    padding: '24px', border: `1px solid ${BORDER}`,
                                }}>
                                    {selectedSubjectData ? (
                                        <KnowledgeTree subject={selectedSubjectData} />
                                    ) : (
                                        <div style={{ padding: 20, textAlign: 'center' }}>
                                            <p style={{ color: BLUE, fontSize: 14 }}>
                                                {t('education.progress.treeSelectPrompt')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── QUIZ HISTORY TAB ── */}
                {activeTab === 'quizzes' && (
                    <AnimatePresence mode="wait">
                        <motion.div
                            key="quiz"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                        >
                            {quizResults.length > 0 && (
                                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                                    {[
                                        { label: t('education.progress.quizSummary.total'), value: quizResults.length, color: BLUE },
                                        {
                                            label: t('education.progress.quizSummary.avg'),
                                            value: `${Math.round(quizResults.reduce((s, r) => s + r.score_percentage, 0) / quizResults.length)}%`,
                                            color: BLUE
                                        },
                                        {
                                            label: t('education.progress.quizSummary.passed'),
                                            value: quizResults.filter(r => r.score_percentage >= 70).length,
                                            color: '#059669'
                                        },
                                    ].map(pill => (
                                        <div key={pill.label} style={{
                                            background: CREAM, borderRadius: 14, padding: '12px 18px',
                                            border: `1px solid ${BORDER}`, display: 'flex', gap: 10, alignItems: 'center',
                                        }}>
                                            <span style={{ fontSize: 20, fontWeight: 900, color: pill.color }}>{pill.value}</span>
                                            <span style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{pill.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <QuizResultsTable results={quizResults} subjectNames={subjectNames} />
                        </motion.div>
                    </AnimatePresence>
                )}

                {/* ── CREDENTIALS & RECORDS TAB ── */}
                {activeTab === 'credentials' && (
                    <AnimatePresence mode="wait">
                        <motion.div
                            key="credentials"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 28 }}
                        >
                            {user?.id && (
                                <>
                                    <CredentialRecordsPanel
                                        userId={user.id}
                                        publicSlug={(userProfile as any)?.public_slug}
                                    />
                                    <div className="empty:hidden">
                                        <CertificateGallery userId={user.id} />
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </AnimatePresence>
                )}
            </div>

            {/* ── NEW SUBJECT MODAL ── */}
            <AnimatePresence>
                {showNewSubjectModal && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 100,
                            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 20,
                        }}
                        onClick={() => !isGenerating && setShowNewSubjectModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: WHITE, borderRadius: 24, padding: 32,
                                maxWidth: 520, width: '100%', maxHeight: '90vh', overflow: 'auto',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                <h2 style={{ fontSize: 22, fontWeight: 900, color: NAVY, margin: 0 }}>
                                    <Sparkles size={20} style={{ display: 'inline', marginRight: 8, color: BLUE }} />
                                    {t('education.soloSetup.title')}
                                </h2>
                                <button onClick={() => setShowNewSubjectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED }}>
                                    <X size={20} />
                                </button>
                            </div>

                            {!generatedPlan ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                                    {/* Demographic Filter */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>{t('common.refine_age_range')}</label>
                                        <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', paddingBottom: 4 }}>
                                            {['all', 'children', 'teenagers', 'young adults', 'adults'].map(d => (
                                                <button
                                                    key={d}
                                                    type="button"
                                                    onClick={() => setDemographicFilter(d)}
                                                    style={{
                                                        padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
                                                        background: demographicFilter === d ? SAGE : CREAM,
                                                        color: demographicFilter === d ? WHITE : SAGE,
                                                        textTransform: 'uppercase', flexShrink: 0,
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    {d === 'all' ? 'All' : d}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Step 1: Topic */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>{t('common.topic_selection_step')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                type="button"
                                                onClick={() => setShowParentDrop(!showParentDrop)}
                                                style={{
                                                    width: '100%', padding: '12px 14px', borderRadius: 12, display: 'flex', alignItems: 'center',
                                                    justifyContent: 'space-between', cursor: 'pointer',
                                                    border: `2px solid ${showParentDrop ? SAGE : BORDER}`, background: WHITE,
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {selectedParentId ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: NAVY, fontWeight: 700 }}>
                                                        <span style={{ fontSize: 18 }}>{eduCategories.find(c => c.id === selectedParentId)?.icon}</span>
                                                        <span>{eduCategories.find(c => c.id === selectedParentId)?.name}</span>
                                                    </span>
                                                ) : (
                                                    <span style={{ color: MUTED }}>{t('common.choose_topic')}</span>
                                                )}
                                                <ChevronRight size={16} color={MUTED} style={{ transform: showParentDrop ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                            </button>

                                            <AnimatePresence>
                                                {showParentDrop && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                                                        style={{
                                                            position: 'absolute', top: 52, left: 0, right: 0, zIndex: 60,
                                                            background: WHITE, borderRadius: 14, border: `2px solid ${BORDER}`,
                                                            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                                                            maxHeight: 200, overflowY: 'auto'
                                                        }}
                                                    >
                                                        {eduCategories.filter(c => !c.parent_id).map(p => (
                                                            <button
                                                                key={p.id}
                                                                onClick={() => { setSelectedParentId(p.id); setSelectedCategoryId(''); setShowParentDrop(false); setShowChildDrop(true) }}
                                                                style={{
                                                                    width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                                                                    background: selectedParentId === p.id ? `${SAGE}10` : 'transparent',
                                                                    border: 'none', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', textAlign: 'left',
                                                                }}
                                                            >
                                                                <span style={{ fontSize: 18 }}>{p.icon}</span>
                                                                <span style={{ flex: 1, fontWeight: 700, color: NAVY, fontSize: 14 }}>{p.name}</span>
                                                            </button>
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>

                                    {/* Step 2: Subject */}
                                    <div style={{ opacity: selectedParentId ? 1 : 0.4, transition: 'opacity 0.2s' }}>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>{t('common.subject_selection_step')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                type="button"
                                                disabled={!selectedParentId}
                                                onClick={() => setShowChildDrop(!showChildDrop)}
                                                style={{
                                                    width: '100%', padding: '12px 14px', borderRadius: 12, display: 'flex', alignItems: 'center',
                                                    justifyContent: 'space-between', cursor: 'pointer',
                                                    border: `2px solid ${showChildDrop ? SAGE : BORDER}`, background: WHITE,
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {selectedCategoryId ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: NAVY, fontWeight: 700 }}>
                                                        <span style={{ fontSize: 18 }}>{eduCategories.find(c => c.id === selectedCategoryId)?.icon}</span>
                                                        <span>{eduCategories.find(c => c.id === selectedCategoryId)?.name}</span>
                                                    </span>
                                                ) : (
                                                    <span style={{ color: MUTED }}>{selectedParentId ? 'Select specific subject...' : 'Pick topic first'}</span>
                                                )}
                                                <ChevronRight size={16} color={MUTED} style={{ transform: showChildDrop ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                            </button>

                                            <AnimatePresence>
                                                {showChildDrop && selectedParentId && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                                                        style={{
                                                            position: 'absolute', top: 52, left: 0, right: 0, zIndex: 50,
                                                            background: WHITE, borderRadius: 14, border: `2px solid ${BORDER}`,
                                                            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                                                            maxHeight: 200, overflowY: 'auto'
                                                        }}
                                                    >
                                                        {/* Parent Overview Option */}
                                                        {(() => {
                                                            const p = eduCategories.find(p => p.id === selectedParentId)
                                                            if (demographicFilter !== 'all' && p?.target_demographics && !p.target_demographics.includes(demographicFilter)) return null
                                                            return (
                                                                <button
                                                                    onClick={() => { setSelectedCategoryId(selectedParentId); setShowChildDrop(false) }}
                                                                    style={{
                                                                        width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                                                                        background: selectedCategoryId === selectedParentId ? `${SAGE}10` : 'transparent',
                                                                        border: 'none', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: 18 }}>{p?.icon}</span>
                                                                    <span style={{ flex: 1, fontWeight: 700, color: NAVY, fontSize: 14 }}>{p?.name} (Overview)</span>
                                                                </button>
                                                            )
                                                        })()}

                                                        {eduCategories
                                                            .filter(c => c.parent_id === selectedParentId)
                                                            .filter(c => demographicFilter === 'all' || (c.target_demographics && c.target_demographics.includes(demographicFilter)))
                                                            .map(cat => (
                                                                <button
                                                                    key={cat.id}
                                                                    onClick={() => { setSelectedCategoryId(cat.id); setShowChildDrop(false) }}
                                                                    style={{
                                                                        width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                                                                        background: selectedCategoryId === cat.id ? `${SAGE}10` : 'transparent',
                                                                        border: 'none', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: 18 }}>{cat.icon}</span>
                                                                    <span style={{ flex: 1, fontWeight: 600, color: NAVY, fontSize: 14 }}>{cat.name}</span>
                                                                </button>
                                                            ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                    {/* Age */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>
                                            {t('education.soloSetup.ageLabel')}
                                        </label>
                                        <input
                                            type="number" min={5} max={100} value={targetAge}
                                            onChange={e => setTargetAge(Number(e.target.value))}
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: 12,
                                                border: `2px solid ${BORDER}`, fontSize: 14, color: NAVY,
                                            }}
                                        />
                                    </div>

                                    {/* Difficulty */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>
                                            {t('education.soloSetup.difficultyLabel')}
                                        </label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            {['Primary', 'Secondary', 'Post-16'].map(level => (
                                                <button
                                                    key={level}
                                                    onClick={() => setDifficultyLevel(level)}
                                                    style={{
                                                        flex: 1, padding: '10px 8px', borderRadius: 12, fontSize: 13,
                                                        fontWeight: 700, cursor: 'pointer',
                                                        border: `2px solid ${difficultyLevel === level ? BLUE : BORDER}`,
                                                        background: difficultyLevel === level ? `${BLUE}10` : WHITE,
                                                        color: difficultyLevel === level ? BLUE : MUTED,
                                                    }}
                                                >
                                                    {level}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Number of lessons */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>
                                            {t('education.soloSetup.lessonsLabel')}
                                        </label>
                                        <input
                                            type="number" min={1} max={30} value={numLessons}
                                            onChange={e => setNumLessons(Number(e.target.value))}
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: 12,
                                                border: `2px solid ${BORDER}`, fontSize: 14, color: NAVY,
                                            }}
                                        />
                                    </div>

                                    {/* Focus areas */}
                                    <div>
                                        <label style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'block' }}>
                                            {t('education.soloSetup.focusLabel')}
                                        </label>
                                        <textarea
                                            value={focusAreas}
                                            onChange={e => setFocusAreas(e.target.value)}
                                            placeholder={t('education.soloSetup.focusPlaceholder')}
                                            rows={3}
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: 12,
                                                border: `2px solid ${BORDER}`, fontSize: 14, color: NAVY,
                                                resize: 'vertical', fontFamily: 'inherit',
                                            }}
                                        />
                                    </div>

                                    {generateError && (
                                        <p style={{ color: '#dc2626', fontSize: 13 }}>{generateError}</p>
                                    )}

                                    <button
                                        onClick={handleGenerateSyllabus}
                                        disabled={!selectedCategoryId || isGenerating}
                                        style={{
                                            width: '100%', padding: '16px', borderRadius: 14,
                                            background: grad, color: WHITE, fontSize: 16, fontWeight: 800,
                                            border: 'none', cursor: (isGenerating || !selectedCategoryId) ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                            boxShadow: '0 8px 25px rgba(74,124,94,0.3)',
                                            marginTop: 10
                                        }}
                                    >
                                        {isGenerating ? (
                                            <><Loader2 size={18} className="animate-spin" /> {t('education.soloSetup.generating')}</>
                                        ) : (
                                            <><Sparkles size={18} /> {t('education.soloSetup.generateBtn')}</>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                /* ── Generated plan review ── */
                                <div>
                                    <p style={{ color: MUTED, fontSize: 13, marginBottom: 16 }}>
                                        {t('education.soloSetup.reviewDescription')}
                                    </p>
                                    <div style={{
                                        background: CREAM, borderRadius: 16, padding: 16, marginBottom: 20,
                                        maxHeight: 300, overflow: 'auto', border: `1px solid ${BORDER}`,
                                    }}>
                                        {(generatedPlan.module_plan || generatedPlan)?.map?.((mod: any) => (
                                            <div key={mod.module_id} style={{ marginBottom: 16 }}>
                                                <h4 style={{ fontWeight: 800, color: NAVY, fontSize: 14, margin: '0 0 8px' }}>
                                                    Module {mod.module_id}: {mod.title}
                                                </h4>
                                                {mod.lessons?.map?.((lesson: any) => (
                                                    <div key={lesson.lesson_id} style={{
                                                        padding: '8px 12px', background: WHITE, borderRadius: 10,
                                                        marginBottom: 6, border: `1px solid ${BORDER}`,
                                                    }}>
                                                        <p style={{ fontWeight: 700, color: NAVY, fontSize: 13, margin: 0 }}>
                                                            L{lesson.lesson_id}: {lesson.title}
                                                        </p>
                                                        <p style={{ color: MUTED, fontSize: 12, margin: '4px 0 0' }}>
                                                            {lesson.learning_objective}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )) || <p style={{ color: MUTED }}>{t('common.plan_generated_successfully')}</p>}
                                    </div>

                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <button
                                            onClick={() => setGeneratedPlan(null)}
                                            style={{
                                                flex: 1, padding: '12px', borderRadius: 12,
                                                border: `2px solid ${BORDER}`, background: WHITE,
                                                color: NAVY, fontWeight: 700, cursor: 'pointer',
                                            }}
                                        >
                                            {t('education.soloSetup.regenerateBtn')}
                                        </button>
                                        <button
                                            onClick={handleSavePlan}
                                            disabled={isGenerating}
                                            style={{
                                                flex: 1, padding: '12px', borderRadius: 12,
                                                border: 'none', background: grad,
                                                color: WHITE, fontWeight: 800, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                            }}
                                        >
                                            {isGenerating ? <Loader2 size={16} className="animate-spin" /> : null}
                                            {t('education.soloSetup.startLearningBtn')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
